import AnaliticaFantasyIcon from '@/components/shared/icons/analitica-fantasy-icon';
import BiwengerIcon from '@/components/shared/icons/biwenger-icon';
import FantasyLaLigaIcon from '@/components/shared/icons/fantasy-la-liga-icon';
import FantasyMarcaIcon from '@/components/shared/icons/FantasyMarcaIcon';
import MisterFantasyIcon from '@/components/shared/icons/MisterFantasyIcon';
import { REDUCERS } from '@/constants/reducers';
import { Context } from '@/context';
import CustomTooltip from '@/DesignSystem/components/tooltip/CustomTooltip';
import useFantasySelected from '@/DesignSystem/hooks/useFantasySelected';
import { FANTASY, FANTASY_MODE } from '@/helpers/biwenger-helper';
import { Box, Select, MenuItem, FormControl } from '@mui/material';
import { useContext, useCallback } from 'react';

const size = 24;

const fantasyOptions = [
    {
        label: 'Analítica Fantasy',
        value: FANTASY.ANALITICA,
        icon: <AnaliticaFantasyIcon width={size} styles={{ borderRadius: '50%' }} />,
    },
    {
        label: 'La Liga Fantasy',
        value: FANTASY.FANTASY,
        icon: <FantasyLaLigaIcon iconWidth={size} iconHeight={size} />,
    },
    {
        label: 'Fantasy Marca',
        value: FANTASY.FANTASY_MARCA,
        icon: <FantasyMarcaIcon width={size} height={size} />,
    },
    {
        label: 'Fantasy Mister',
        value: FANTASY.MISTER_FANTASY,
        icon: <MisterFantasyIcon width={size} height={size} />,
    },
    {
        label: 'Fantasy Biwenger',
        value: FANTASY.BIWENGER,
        icon: <BiwengerIcon width={size} height={size} />,
        modes: [
            {
                label: 'Diario AS',
                value: FANTASY_MODE.DIARIO_AS,
                icon: <BiwengerIcon width={size} height={size} />,
            },
            {
                label: 'Sofascore',
                value: FANTASY_MODE.SOFASCORE,
                icon: <BiwengerIcon width={size} height={size} />,
            },
            {
                label: 'Media AS/Sofa',
                value: FANTASY_MODE.AVERAGE,
                icon: <BiwengerIcon width={size} height={size} />,
            },
            {
                label: 'Estadísticas',
                value: FANTASY_MODE.STATS,
                icon: <BiwengerIcon width={size} height={size} />,
            },
            {
                label: 'Social',
                value: FANTASY_MODE.SOCIAL,
                icon: <BiwengerIcon width={size} height={size} />,
            },
        ],
    },
];

const LineupFantasySelectorConfirmed = ({ fantasyEnabled }) => {
    const { dispatch } = useContext(Context);
    const { confirmedFantasy, confirmedFantasyMode } = useFantasySelected();

    const handleClick = useCallback(
        (value, mode = FANTASY_MODE.DIARIO_AS, event) => {
            event?.stopPropagation();
            dispatch({
                type: REDUCERS.UPDATE_PREFERENCES,
                payload: { confirmedFantasy: value, confirmedFantasyMode: mode },
            });
        },
        [dispatch]
    );

    const handleModeChange = useCallback(
        (event) => {
            const newMode = event.target.value;
            dispatch({
                type: REDUCERS.UPDATE_PREFERENCES,
                payload: { confirmedFantasyMode: newMode },
            });
        },
        [dispatch]
    );

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            {/* Dropdown de submodos de Biwenger (arriba, solo si está seleccionado) */}
            {confirmedFantasy === FANTASY.BIWENGER && (
                <Box sx={{ display: 'flex', justifyContent: 'flex-start' }}>
                    <FormControl size="small" sx={{ minWidth: 120 }}>
                        <Select
                            value={confirmedFantasyMode}
                            onChange={handleModeChange}
                            displayEmpty
                            sx={{
                                height: 32,
                                fontSize: '0.75rem',
                                '& .MuiSelect-select': {
                                    padding: '4px 8px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 0.5,
                                },
                                '& .MuiOutlinedInput-notchedOutline': {
                                    borderColor: 'rgba(255, 255, 255, 0.23)',
                                },
                                '&:hover .MuiOutlinedInput-notchedOutline': {
                                    borderColor: 'rgba(255, 255, 255, 0.4)',
                                },
                                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                    borderColor: 'primary.main',
                                },
                                backgroundColor: 'rgba(255, 255, 255, 0.05)',
                                color: 'text.primary',
                            }}
                        >
                            {fantasyOptions
                                .find((option) => option.value === FANTASY.BIWENGER)
                                ?.modes?.map((mode) => (
                                    <MenuItem
                                        key={mode.value}
                                        value={mode.value}
                                        sx={{ fontSize: '0.75rem' }}
                                    >
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                            {mode.icon}
                                            {mode.label}
                                        </Box>
                                    </MenuItem>
                                ))}
                        </Select>
                    </FormControl>
                </Box>
            )}

            {/* Modos principales (abajo, siempre centrados en la misma posición) */}
            <Box
                sx={{
                    display: 'flex',
                    gap: 0.5,
                    alignItems: 'center',
                    width: '100%',
                }}
            >
                {fantasyOptions.map((option) => {
                    const isSelected = confirmedFantasy === option.value;
                    const isEnabled =
                        (option.value === FANTASY.BIWENGER && fantasyEnabled?.isBiwengerEnabled) ||
                        (option.value === FANTASY.FANTASY && fantasyEnabled?.isFantasyLaLigaEnabled) ||
                        (option.value === FANTASY.FANTASY_MARCA && fantasyEnabled?.isFantasyMarcaEnabled) ||
                        (option.value === FANTASY.MISTER_FANTASY && fantasyEnabled?.isMisterFantasyEnabled) ||
                        (option.value === FANTASY.ANALITICA && fantasyEnabled?.isAnaliticaFantasyEnabled);

                    if (!isEnabled) return null;
                    return (
                        <Box key={option.value}>
                            <CustomTooltip label={option.label}>
                                <Box
                                    sx={{
                                        cursor: 'pointer',
                                        opacity: isSelected ? 1 : 0.35,
                                        transition: 'opacity 0.2s ease',
                                        '&:hover': {
                                            opacity: isSelected ? 1 : 0.6,
                                        },
                                    }}
                                    onClick={(e) => handleClick(option.value, FANTASY_MODE.DIARIO_AS, e)}
                                >
                                    {option.icon}
                                </Box>
                            </CustomTooltip>
                        </Box>
                    );
                })}
            </Box>
        </Box>
    );
};

export default LineupFantasySelectorConfirmed;
